import { SearchFilterV4 } from "@/types/SearchFilter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@components/ui/dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { DialogProps } from "@radix-ui/react-dialog";
import { Button } from "@components/ui/button";
import { useEffect, useMemo } from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@components/ui/input-group";
import { SearchIcon } from "lucide-react";
import { Field, FieldError, FieldLabel } from "@components/ui/field";
import { Controller, SubmitHandler, useFormState } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const formSchema = z.object({
  freeText: z.string(),
});

export default function DialogFreeText({
  onChange,
  value,
  ...rest
}: {
  onChange: (value: SearchFilterV4, clear?: boolean) => void;
  value: SearchFilterV4["freetext"];
} & DialogProps) {
  const { t } = useLingui();

  // Local state
  const defaultValue = useMemo(() => {
    return value || "";
  }, [value]);

  // Form state
  const form = useFormCompat<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      freeText: defaultValue,
    },
  });

  const formState = useFormState({ control: form.control });

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = function (
    values
  ) {
    onChange({
      freetext: values.freeText || undefined,
    });

    rest.onOpenChange?.(false);
  };

  // Side-effects
  useEffect(() => {
    form.reset({
      freeText: defaultValue,
    });
  }, [defaultValue, rest.open, form]);

  return (
    <Dialog {...rest}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Frisök</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Sök fritt bland omnämnandes innehåll.</Trans>
          </DialogDescription>
        </DialogHeader>

        <form id="freetext-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <Controller
            control={form.control}
            name="freeText"
            render={({ field, fieldState }) => {
              const id = "freetext";

              return (
                <Field>
                  <FieldLabel className="sr-only" id={id}>
                    <Trans>Sökterm</Trans>
                  </FieldLabel>

                  <InputGroup>
                    <InputGroupAddon>
                      <SearchIcon />
                    </InputGroupAddon>
                    <InputGroupInput
                      {...field}
                      data-testid="filter-bar--freetext-input"
                      id={id}
                      autoComplete="off"
                      aria-invalid={fieldState.invalid}
                      placeholder={t`Sök`}
                    />
                  </InputGroup>

                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              );
            }}
          />
        </form>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            <Trans>Avbryt</Trans>
          </DialogClose>
          <Button
            type="submit"
            form="freetext-form"
            data-testid="filter-bar--submit"
            disabled={!formState.isDirty}
          >
            <Trans>Applicera</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
